import type { Lot, Offer, Asset, WantAd, DealMessage, APIResponse } from '@cherry/shared';

export interface TradeActivity {
  lotId: number;
  seller: string;
  buyer: string;
  sellerAssets: Asset[];
  buyerAssets: Asset[];
  filledAt: string;
}

export interface InAppNotification {
  id: number;
  eventType: string;
  title: string;
  message: string;
  lotId: number | null;
  offerId: number | null;
  read: boolean;
  createdAt: string;
}

export interface NotificationsResponse {
  unreadCount: number;
  notifications: InAppNotification[];
}

export interface TraderReputation {
  address: string;
  tradesAsSeller: number;
  tradesAsBuyer: number;
  totalTrades: number;
  totalOffersMade: number;
  completionRate: number;
  totalVolumeNano: string;
  totalVolumeTon: string;
}

export interface UserTrade extends TradeActivity {
  role: 'seller' | 'buyer';
}

export interface ReferralStats {
  referralCount: number;
  totalEarnedNano: string;
  totalEarnedTon: string;
  referralLink: string;
}

export interface SavedSearch {
  id: number;
  walletAddress: string;
  name: string;
  queryParams: Record<string, string | undefined>;
  notifyEnabled: boolean;
  lastMatchedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GiftCollectionSummary {
  collectionName: string;
  count: number;
}

export interface GiftAttributeValues {
  models: string[];
  backdrops: string[];
  symbols: string[];
}

export interface CollectionTradeStats {
  collectionName: string;
  tradeCount: number;
  lastTradeTonNano: string | null;
  avgTradeTonNano: string | null;
  trend: 'up' | 'down' | 'stable';
}

export interface CollectionFloorPrice {
  collectionName: string;
  floorTonNano: string;
  activeLotCount: number;
}

export interface MarketSummary {
  totalTrades: number;
  totalVolumeTonNano: string;
  last24hTrades: number;
  last7dTrades: number;
}

export interface MarketStatsResponse {
  tradeStats: CollectionTradeStats[];
  floorPrices: CollectionFloorPrice[];
  summary: MarketSummary;
}

export type PointsTier = 'bronze' | 'silver' | 'gold' | 'cherry_elite';

export interface PointsProfile {
  walletAddress: string;
  totalPoints: number;
  tier: PointsTier;
  nextTier: { tier: PointsTier; pointsNeeded: number } | null;
}

export interface PointsLogEntry {
  eventType: string;
  points: number;
  lotId: number | null;
  createdAt: string;
}

export interface LeaderboardEntry {
  walletAddress: string;
  totalPoints: number;
  tier: PointsTier;
  rank: number;
}

export interface TradingEventInfo {
  id: number;
  title: string;
  description: string;
  bannerText: string;
  bannerColor: string;
  feeNote: string | null;
  startsAt: string;
  endsAt: string;
}

export interface ValuationResponse {
  estimatedValueUsd: number;
  tonPriceUsd: number | null;
  breakdown: Array<{ kind: string; valueUsd: number | null }>;
  hasUnpricedAssets: boolean;
  recommendation: {
    recommendedTonNano: string;
    recommendedTon: number;
    estimatedFeeUsd: number;
  } | null;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

async function fetchAPI<T>(endpoint: string): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`);
  
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  
  const data = (await response.json()) as APIResponse<T>;
  
  if (!data.ok) {
    throw new Error(data.error || 'Unknown API error');
  }
  
  return data.data;
}

export interface LotsResponse {
  lots: Lot[];
  total: number;
}

export interface LotDetailResponse {
  lot: Lot;
  offers: Offer[];
}

export const api = {
  getLots: async (params?: {
    query?: string;
    assetKind?: string;
    jettonMaster?: string;
    collection?: string;
    collectionName?: string;
    giftModel?: string;
    status?: string;
    sort?: string;
    order?: string;
    page?: number;
    pageSize?: number;
  }): Promise<Lot[]> => {
    const searchParams = new URLSearchParams();
    if (params?.query) searchParams.append('query', params.query);
    if (params?.assetKind) searchParams.append('assetKind', params.assetKind);
    if (params?.jettonMaster) searchParams.append('jettonMaster', params.jettonMaster);
    if (params?.collection) searchParams.append('collection', params.collection);
    if (params?.collectionName) searchParams.append('collectionName', params.collectionName);
    if (params?.giftModel) searchParams.append('giftModel', params.giftModel);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.order) searchParams.append('order', params.order);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    
    const queryString = searchParams.toString();
    const endpoint = `/api/lots${queryString ? `?${queryString}` : ''}`;
    
    return fetchAPI<Lot[]>(endpoint);
  },
  
  getLot: async (lotId: number): Promise<LotDetailResponse> => {
    return fetchAPI<LotDetailResponse>(`/api/lot/${lotId}`);
  },
  
  getUserLots: async (address: string): Promise<Lot[]> => {
    return fetchAPI<Lot[]>(`/api/user/${address}/lots`);
  },
  
  getUserOffers: async (address: string): Promise<Offer[]> => {
    return fetchAPI<Offer[]>(`/api/user/${address}/offers`);
  },
  
  getHealth: async (): Promise<{ timestamp: string }> => {
    return fetchAPI<{ timestamp: string }>('/health');
  },

  getActivity: async (limit = 20): Promise<TradeActivity[]> => {
    return fetchAPI<TradeActivity[]>(`/api/activity?limit=${limit}`);
  },

  getJettonMetadata: async (master: string): Promise<{
    master: string;
    name: string | null;
    symbol: string | null;
    decimals: number;
    imageUrl: string | null;
  }> => {
    return fetchAPI(`/api/metadata/jetton/${encodeURIComponent(master)}`);
  },

  getJettonMetadataBatch: async (masters: string[]): Promise<Record<string, {
    master: string;
    name: string | null;
    symbol: string | null;
    decimals: number;
    imageUrl: string | null;
  }>> => {
    return fetchAPI(`/api/metadata/jettons?masters=${masters.map(encodeURIComponent).join(',')}`);
  },

  getNotifications: async (address: string): Promise<NotificationsResponse> => {
    return fetchAPI<NotificationsResponse>(`/api/notifications/${encodeURIComponent(address)}`);
  },

  markNotificationsRead: async (address: string, ids?: number[]): Promise<void> => {
    await fetch(`${API_BASE_URL}/api/notifications/${encodeURIComponent(address)}/read`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ids ? { ids } : {}),
    });
  },

  clearNotifications: async (address: string): Promise<void> => {
    await fetch(`${API_BASE_URL}/api/notifications/${encodeURIComponent(address)}`, {
      method: 'DELETE',
    });
  },

  getReputation: async (address: string): Promise<TraderReputation> => {
    return fetchAPI<TraderReputation>(`/api/reputation/${encodeURIComponent(address)}`);
  },

  getReputationBatch: async (addresses: string[]): Promise<Record<string, TraderReputation>> => {
    return fetchAPI<Record<string, TraderReputation>>(
      `/api/reputation/batch?addresses=${addresses.map(encodeURIComponent).join(',')}`,
    );
  },

  getUserTrades: async (address: string): Promise<UserTrade[]> => {
    return fetchAPI<UserTrade[]>(`/api/user/${encodeURIComponent(address)}/trades`);
  },

  getReferralStats: async (address: string): Promise<ReferralStats> => {
    return fetchAPI<ReferralStats>(`/api/referral/${encodeURIComponent(address)}/stats`);
  },

  trackReferral: async (referrerAddress: string, referredAddress: string): Promise<void> => {
    await fetch(`${API_BASE_URL}/api/referral/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ referrerAddress, referredAddress }),
    });
  },

  estimateValuation: async (assets: Array<{ kind: string; amountNano?: string; amount?: string; master?: string; address?: string }>): Promise<ValuationResponse> => {
    const res = await fetch(`${API_BASE_URL}/api/valuation/estimate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assets }),
    });
    const data = (await res.json()) as APIResponse<ValuationResponse>;
    if (!data.ok) throw new Error(data.error || 'Valuation failed');
    return data.data;
  },

  getSavedSearches: async (address: string): Promise<SavedSearch[]> => {
    return fetchAPI<SavedSearch[]>(`/api/searches/${encodeURIComponent(address)}`);
  },

  createSavedSearch: async (
    address: string,
    body: { name: string; queryParams: Record<string, string | undefined>; notifyEnabled: boolean },
  ): Promise<SavedSearch> => {
    const res = await fetch(`${API_BASE_URL}/api/searches/${encodeURIComponent(address)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = (await res.json()) as APIResponse<SavedSearch>;
    if (!data.ok) throw new Error(data.error || 'Failed to save search');
    return data.data;
  },

  updateSavedSearch: async (
    address: string,
    searchId: number,
    body: { name?: string; notifyEnabled?: boolean },
  ): Promise<SavedSearch> => {
    const res = await fetch(
      `${API_BASE_URL}/api/searches/${encodeURIComponent(address)}/${searchId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    const data = (await res.json()) as APIResponse<SavedSearch>;
    if (!data.ok) throw new Error(data.error || 'Failed to update search');
    return data.data;
  },

  deleteSavedSearch: async (address: string, searchId: number): Promise<void> => {
    const res = await fetch(
      `${API_BASE_URL}/api/searches/${encodeURIComponent(address)}/${searchId}`,
      { method: 'DELETE' },
    );
    const data = (await res.json()) as APIResponse<void>;
    if (!data.ok) throw new Error(data.error || 'Failed to delete search');
  },

  getWantAds: async (params?: {
    status?: string;
    assetKind?: string;
    query?: string;
    page?: number;
    pageSize?: number;
  }): Promise<WantAd[]> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.assetKind) searchParams.append('assetKind', params.assetKind);
    if (params?.query) searchParams.append('query', params.query);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    const qs = searchParams.toString();
    return fetchAPI<WantAd[]>(`/api/want-ads${qs ? `?${qs}` : ''}`);
  },

  getWantAd: async (adId: number): Promise<WantAd> => {
    return fetchAPI<WantAd>(`/api/want-ad/${adId}`);
  },

  createWantAd: async (body: {
    buyerAddress: string;
    title: string;
    description: string;
    budgetTonNano?: string;
    assetKinds: string[];
    preferredCollections?: string[];
    preferredAttributes?: Record<string, string>;
    expiresInDays: number;
  }): Promise<WantAd> => {
    const res = await fetch(`${API_BASE_URL}/api/want-ads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = (await res.json()) as APIResponse<WantAd>;
    if (!data.ok) throw new Error(data.error || 'Failed to create want ad');
    return data.data;
  },

  closeWantAd: async (adId: number, buyerAddress: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/api/want-ad/${adId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ buyerAddress }),
    });
    const data = (await res.json()) as APIResponse<{ closed: boolean }>;
    if (!data.ok) throw new Error(data.error || 'Failed to close want ad');
  },

  getDealMessages: async (lotId: number, after?: number): Promise<DealMessage[]> => {
    const params = new URLSearchParams();
    if (after) params.append('after', after.toString());
    const qs = params.toString();
    return fetchAPI<DealMessage[]>(`/api/lot/${lotId}/messages${qs ? `?${qs}` : ''}`);
  },

  sendDealMessage: async (lotId: number, senderAddress: string, content: string): Promise<DealMessage> => {
    const res = await fetch(`${API_BASE_URL}/api/lot/${lotId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ senderAddress, content }),
    });
    const data = (await res.json()) as APIResponse<DealMessage>;
    if (!data.ok) throw new Error(data.error || 'Failed to send message');
    return data.data;
  },

  getGiftCollections: async (): Promise<GiftCollectionSummary[]> => {
    return fetchAPI<GiftCollectionSummary[]>('/api/gift-collections');
  },

  getGiftCollectionAttributes: async (name: string): Promise<GiftAttributeValues> => {
    return fetchAPI<GiftAttributeValues>(`/api/gift-collections/${encodeURIComponent(name)}/attributes`);
  },

  // Watchers (B-400)
  getWatcherInfo: async (lotId: number, viewerAddress?: string): Promise<{ count: number; watching: boolean }> => {
    const qs = viewerAddress ? `?address=${encodeURIComponent(viewerAddress)}` : '';
    return fetchAPI<{ count: number; watching: boolean }>(`/api/lots/${lotId}/watchers${qs}`);
  },

  watchLot: async (lotId: number, address: string): Promise<{ count: number; watching: boolean }> => {
    const res = await fetch(`${API_BASE_URL}/api/lots/${lotId}/watch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address }),
    });
    const data = (await res.json()) as APIResponse<{ count: number; watching: boolean }>;
    if (!data.ok) throw new Error(data.error || 'Failed to watch lot');
    return data.data;
  },

  unwatchLot: async (lotId: number, address: string): Promise<{ count: number; watching: boolean }> => {
    const res = await fetch(`${API_BASE_URL}/api/lots/${lotId}/watch`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address }),
    });
    const data = (await res.json()) as APIResponse<{ count: number; watching: boolean }>;
    if (!data.ok) throw new Error(data.error || 'Failed to unwatch lot');
    return data.data;
  },

  // Auto-accept/decline thresholds (B-404)
  setLotThresholds: async (
    lotId: number,
    sellerAddress: string,
    thresholds: { autoAcceptTon?: string | null; autoDeclineTon?: string | null },
  ): Promise<{ autoAcceptTon: string | null; autoDeclineTon: string | null }> => {
    const res = await fetch(`${API_BASE_URL}/api/lots/${lotId}/thresholds`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Wallet-Address': sellerAddress,
      },
      body: JSON.stringify(thresholds),
    });
    const data = (await res.json()) as APIResponse<{ autoAcceptTon: string | null; autoDeclineTon: string | null }>;
    if (!data.ok) throw new Error(data.error || 'Failed to set thresholds');
    return data.data;
  },

  // Market data (B-402, B-407)
  getMarketStats: async (collection?: string): Promise<MarketStatsResponse> => {
    const qs = collection ? `?collection=${encodeURIComponent(collection)}` : '';
    return fetchAPI<MarketStatsResponse>(`/api/market/stats${qs}`);
  },

  getFloorPrices: async (): Promise<CollectionFloorPrice[]> => {
    return fetchAPI<CollectionFloorPrice[]>('/api/market/floor-prices');
  },

  getMarketDemand: async (assetKinds?: string[], collections?: string[]): Promise<{ matchingWantAds: number }> => {
    const params = new URLSearchParams();
    if (assetKinds?.length) params.append('assetKinds', assetKinds.join(','));
    if (collections?.length) params.append('collections', collections.join(','));
    const qs = params.toString();
    return fetchAPI<{ matchingWantAds: number }>(`/api/market/demand${qs ? `?${qs}` : ''}`);
  },

  // Cherry Points (B-405)
  getPoints: async (address: string): Promise<PointsProfile> => {
    return fetchAPI<PointsProfile>(`/api/points/${encodeURIComponent(address)}`);
  },

  getPointsHistory: async (address: string): Promise<PointsLogEntry[]> => {
    return fetchAPI<PointsLogEntry[]>(`/api/points/${encodeURIComponent(address)}/history`);
  },

  getLeaderboard: async (): Promise<LeaderboardEntry[]> => {
    return fetchAPI<LeaderboardEntry[]>('/api/leaderboard');
  },

  // Trading events (B-406)
  getActiveEvents: async (): Promise<TradingEventInfo[]> => {
    return fetchAPI<TradingEventInfo[]>('/api/events/active');
  },
};
